import { useEffect, useMemo, useState } from "react";
import { invoiceApi } from "../../services/invoiceApi";
import { settingsApi } from "../../services/settingsApi";

const money = (value) => Number(Number(value || 0).toFixed(2));

const emptyItem = () => ({
  costPriceId: "",
  description: "",
  quantity: 1,
  unitPrice: 0,
  taxRate: "",
});

const paymentModes = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "cheque", label: "Cheque" },
  { value: "online", label: "Online" },
  { value: "other", label: "Other" },
];

const statusOptions = [
  { value: "draft", label: "Draft" },
  { value: "pending", label: "Pending" },
  { value: "partial", label: "Partial" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
];

const currencyOptions = ["USD", "GBP", "PKR", "EUR", "AED", "SAR"];

export default function InvoiceFormModal({
  open,
  onClose,
  onSaved,
  invoice,
  vendors = [],
  costPrices = [],
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [appSettings, setAppSettings] = useState({
    invoiceShowLogo: true,
    invoiceShowTitle: true,
    invoiceTitle: "INVOICE",
    invoiceCurrency: "USD",
    invoiceTaxEnabled: false,
    invoiceTaxRate: 0,
    invoiceDiscountEnabled: false,
    invoiceDiscountRate: 0,
    logoDataUrl: "",
  });

  const [vendorId, setVendorId] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [paymentMode, setPaymentMode] = useState("bank_transfer");
  const [status, setStatus] = useState("pending");
  const [currency, setCurrency] = useState("USD");

  const [taxMode, setTaxMode] = useState("percent");
  const [taxRate, setTaxRate] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);

  const [discountAmount, setDiscountAmount] = useState(0);
  const [paidAmount, setPaidAmount] = useState(0);
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([emptyItem()]);

  useEffect(() => {
    if (!open) return;

    const loadSettings = async () => {
      try {
        const res = await settingsApi.getMy();
        const s = res.data?.settings || {};
        setAppSettings({
          invoiceShowLogo: Boolean(s.invoiceShowLogo),
          invoiceShowTitle: Boolean(s.invoiceShowTitle),
          invoiceTitle: s.invoiceTitle || "INVOICE",
          invoiceCurrency: s.invoiceCurrency || "USD",
          invoiceTaxEnabled: Boolean(s.invoiceTaxEnabled),
          invoiceTaxRate: Number(s.invoiceTaxRate || 0),
          invoiceDiscountEnabled: Boolean(s.invoiceDiscountEnabled),
          invoiceDiscountRate: Number(s.invoiceDiscountRate || 0),
          logoDataUrl: s.logoDataUrl || "",
        });

        if (!invoice) {
          setCurrency(s.invoiceCurrency || "USD");
          setTaxMode(s.invoiceTaxEnabled ? "percent" : "percent");
          setTaxRate(Number(s.invoiceTaxRate || 0));
          setDiscountAmount(0);
        }
      } catch (err) {
        // keep defaults
      }
    };

    loadSettings();

    if (invoice) {
      setVendorId(invoice.vendorId || "");
      setSupplierName(invoice.supplierName || "");
      setInvoiceDate(invoice.invoiceDate || "");
      setDueDate(invoice.dueDate || "");
      setPaymentMode(invoice.paymentMode || "bank_transfer");
      setStatus(invoice.status || "pending");
      setCurrency(invoice.currency || "USD");

      setTaxAmount(Number(invoice.taxAmount || 0));
      setTaxMode(Number(invoice.taxAmount || 0) > 0 ? "amount" : "percent");
      setTaxRate(Number(invoice.taxRate || 0) || 0);

      setDiscountAmount(Number(invoice.discountAmount || 0));
      setPaidAmount(Number(invoice.paidAmount || 0));
      setNotes(invoice.notes || "");
      setItems(
        invoice.items?.length
          ? invoice.items.map((item) => ({
              costPriceId: item.costPriceId || "",
              description: item.description || "",
              quantity: Number(item.quantity || 1),
              unitPrice: Number(item.unitPrice || 0),
              taxRate:
                item.taxRate !== undefined && item.taxRate !== null
                  ? Number(item.taxRate)
                  : "",
            }))
          : [emptyItem()]
      );
    } else {
      setVendorId("");
      setSupplierName("");
      setInvoiceDate(new Date().toISOString().slice(0, 10));
      setDueDate("");
      setPaymentMode("bank_transfer");
      setStatus("pending");
      setCurrency((prev) => prev || "USD");
      setTaxMode("percent");
      setTaxRate(0);
      setTaxAmount(0);
      setDiscountAmount(0);
      setPaidAmount(0);
      setNotes("");
      setItems([emptyItem()]);
    }

    setError("");
  }, [open, invoice]);

  const totals = useMemo(() => {
    let subtotal = 0;
    let totalTax = 0;

    items.forEach((item) => {
      const qty = Number(item.quantity || 0);
      const price = Number(item.unitPrice || 0);
      const base = qty * price;
      const rate =
        item.taxRate === "" || item.taxRate === null || item.taxRate === undefined
          ? taxMode === "percent"
            ? Number(taxRate || 0)
            : 0
          : Number(item.taxRate || 0);

      subtotal += base;
      totalTax += base * (rate / 100);
    });

    subtotal = money(subtotal);
    totalTax = money(totalTax);

    const discount =
      appSettings.invoiceDiscountEnabled && Number(appSettings.invoiceDiscountRate || 0) > 0
        ? money((subtotal * Number(appSettings.invoiceDiscountRate || 0)) / 100)
        : money(discountAmount || 0);

    const invoiceTax =
      appSettings.invoiceTaxEnabled && Number(appSettings.invoiceTaxRate || 0) > 0
        ? money((subtotal * Number(appSettings.invoiceTaxRate || 0)) / 100)
        : totalTax;

    const totalAmount = money(subtotal + invoiceTax - discount);
    const balanceAmount = money(totalAmount - Number(paidAmount || 0));

    return {
      subtotal,
      tax: invoiceTax,
      discount,
      totalAmount,
      balanceAmount,
    };
  }, [
    items,
    taxRate,
    taxMode,
    discountAmount,
    paidAmount,
    appSettings.invoiceTaxEnabled,
    appSettings.invoiceTaxRate,
    appSettings.invoiceDiscountEnabled,
    appSettings.invoiceDiscountRate,
  ]);

  const setItemField = (index, field, value) => {
    setItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;

        let updated = { ...item, [field]: value };

        if (field === "costPriceId") {
          const selected = costPrices.find((cp) => String(cp.id) === String(value));
          if (selected) {
            updated.description =
              selected.productName ||
              selected.name ||
              selected.title ||
              selected.description ||
              "";
            updated.unitPrice = Number(
              selected.price ?? selected.unitPrice ?? selected.costPrice ?? selected.total ?? 0
            );
            if (!updated.taxRate && appSettings.invoiceTaxEnabled) {
              updated.taxRate = appSettings.invoiceTaxRate || 0;
            }
          }
        }

        return updated;
      })
    );
  };

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (index) =>
    setItems((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      if (!invoiceDate) throw new Error("Invoice date is required");

      const cleanedItems = items
        .map((item) => {
          const quantity = Number(item.quantity || 0);
          const unitPrice = Number(item.unitPrice || 0);
          const lineBase = money(quantity * unitPrice);

          const resolvedTaxRate =
            item.taxRate === "" || item.taxRate === null || item.taxRate === undefined
              ? appSettings.invoiceTaxEnabled
                ? Number(appSettings.invoiceTaxRate || 0)
                : taxMode === "percent"
                ? Number(taxRate || 0)
                : 0
              : Number(item.taxRate || 0);

          return {
            costPriceId: item.costPriceId ? Number(item.costPriceId) : null,
            description: (item.description || "").trim(),
            quantity,
            unitPrice,
            taxRate: resolvedTaxRate,
            taxAmount: money(lineBase * (resolvedTaxRate / 100)),
          };
        })
        .filter((item) => item.description && item.quantity > 0);

      if (cleanedItems.length === 0) {
        throw new Error("Add at least one valid invoice item");
      }

      const payload = {
        vendorId: vendorId ? Number(vendorId) : null,
        supplierName: supplierName.trim() || null,
        invoiceDate,
        dueDate: dueDate || null,
        paymentMode,
        status,
        currency,
        taxMode: appSettings.invoiceTaxEnabled ? "percent" : taxMode,
        taxRate:
          appSettings.invoiceTaxEnabled || taxMode === "percent"
            ? Number(appSettings.invoiceTaxRate || taxRate || 0)
            : null,
        taxAmount:
          appSettings.invoiceTaxEnabled
            ? money(totals.tax)
            : taxMode === "amount"
            ? Number(taxAmount || 0)
            : money(totals.tax),
        discountAmount: appSettings.invoiceDiscountEnabled
          ? money(totals.discount)
          : Number(discountAmount || 0),
        paidAmount: Number(paidAmount || 0),
        notes: notes.trim() || null,
        items: cleanedItems,
      };

      const res = invoice
        ? await invoiceApi.update(invoice.id, payload)
        : await invoiceApi.create(payload);

      onSaved?.(res.data?.message || "Saved successfully");
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to save invoice");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 px-4 py-6">
      <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">
              {invoice ? "Edit Invoice" : "New Invoice"}
            </h2>
            <p className="text-sm text-slate-500">
              Uses your saved settings for logo, title, currency, and tax defaults.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Close
          </button>
        </div>

        <form onSubmit={submit} className="space-y-6 p-6">
          {error ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Vendor</label>
              <select
                value={vendorId}
                onChange={(e) => setVendorId(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500"
              >
                <option value="">Select vendor</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name || v.companyName || `Vendor ${v.id}`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Supplier Name</label>
              <input
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500"
                placeholder="Client or supplier name"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Invoice Date</label>
              <input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Payment Mode</label>
              <select
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500"
              >
                {paymentModes.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500"
              >
                {statusOptions.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Currency</label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500"
              >
                {currencyOptions.map((cur) => (
                  <option key={cur} value={cur}>
                    {cur}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Paid Amount</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={paidAmount}
                onChange={(e) => setPaidAmount(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">Tax Mode</label>
              <select
                value={taxMode}
                onChange={(e) => setTaxMode(e.target.value)}
                disabled={appSettings.invoiceTaxEnabled}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500 disabled:bg-slate-100"
              >
                <option value="percent">Tax %</option>
                <option value="amount">Manual Amount</option>
              </select>
            </div>

            {taxMode === "percent" || appSettings.invoiceTaxEnabled ? (
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Tax % {appSettings.invoiceTaxEnabled ? "(from settings)" : ""}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={appSettings.invoiceTaxEnabled ? appSettings.invoiceTaxRate : taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  disabled={appSettings.invoiceTaxEnabled}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500 disabled:bg-slate-100"
                  placeholder="0"
                />
              </div>
            ) : (
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Tax Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={taxAmount}
                  onChange={(e) => setTaxAmount(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500"
                  placeholder="0.00"
                />
              </div>
            )}

            <div className="xl:col-span-4">
              <label className="mb-2 block text-sm font-medium text-slate-700">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows="3"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500"
                placeholder="Optional notes"
              />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-100">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <h3 className="text-sm font-semibold text-slate-700">Invoice Items</h3>
              <button
                type="button"
                onClick={addItem}
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                + Add Item
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Cost Price</th>
                    <th className="px-4 py-3">Description</th>
                    <th className="px-4 py-3">Qty</th>
                    <th className="px-4 py-3">Unit Price</th>
                    <th className="px-4 py-3">Tax %</th>
                    <th className="px-4 py-3">Line Total</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => {
                    const base = money(item.quantity) * money(item.unitPrice);
                    const rate =
                      item.taxRate === "" || item.taxRate === null || item.taxRate === undefined
                        ? appSettings.invoiceTaxEnabled
                          ? Number(appSettings.invoiceTaxRate || 0)
                          : Number(taxRate || 0)
                        : Number(item.taxRate || 0);
                    const lineTax = base * (rate / 100);
                    const lineTotal = money(base + lineTax);

                    return (
                      <tr key={index} className="border-t border-slate-100">
                        <td className="px-4 py-3">
                          <select
                            value={item.costPriceId}
                            onChange={(e) => setItemField(index, "costPriceId", e.target.value)}
                            className="w-full rounded-xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-500"
                          >
                            <option value="">Manual</option>
                            {costPrices.map((cp) => (
                              <option key={cp.id} value={cp.id}>
                                {cp.productName || cp.name || cp.title || cp.description || `Item ${cp.id}`}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td className="px-4 py-3">
                          <input
                            value={item.description}
                            onChange={(e) => setItemField(index, "description", e.target.value)}
                            className="w-full rounded-xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-500"
                            placeholder="Description"
                          />
                        </td>

                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) => setItemField(index, "quantity", e.target.value)}
                            className="w-24 rounded-xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-500"
                          />
                        </td>

                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unitPrice}
                            onChange={(e) => setItemField(index, "unitPrice", e.target.value)}
                            className="w-28 rounded-xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-500"
                          />
                        </td>

                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={appSettings.invoiceTaxEnabled ? appSettings.invoiceTaxRate : item.taxRate}
                            onChange={(e) => setItemField(index, "taxRate", e.target.value)}
                            disabled={appSettings.invoiceTaxEnabled}
                            className="w-24 rounded-xl border border-slate-200 px-3 py-2 outline-none focus:border-blue-500 disabled:bg-slate-100"
                          />
                        </td>

                        <td className="px-4 py-3 font-medium text-slate-700">
                          {currency} {lineTotal.toFixed(2)}
                        </td>

                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-2xl bg-slate-50 p-4">
              <p className="text-sm text-slate-500">Subtotal</p>
              <p className="mt-2 text-xl font-semibold text-slate-900">
                {currency} {totals.subtotal.toFixed(2)}
              </p>
            </div>
            <div className="rounded-2xl bg-slate-50 p-4">
              <p className="text-sm text-slate-500">Tax</p>
              <p className="mt-2 text-xl font-semibold text-slate-900">
                {currency} {totals.tax.toFixed(2)}
              </p>
            </div>
            <div className="rounded-2xl bg-slate-50 p-4">
              <p className="text-sm text-slate-500">Amount Due</p>
              <p className="mt-2 text-xl font-semibold text-slate-900">
                {currency} {totals.totalAmount.toFixed(2)}
              </p>
            </div>
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-slate-100 pt-4 md:flex-row md:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {saving ? "Saving..." : invoice ? "Update Invoice" : "Create Invoice"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}